import React from "react";
import { createPortal } from "react-dom";
import { TrashIcon, XMarkIcon } from "@heroicons/react/24/outline";
import Spinner from "../../../ui/Spinner";

export default function DeleteAccountModal({
  step,
  setStep,
  onClose,
  onConfirm,
  deleteReason,
  setDeleteReason,
  deleteInput,
  setDeleteInput,
  deleting = false,
}) {
  const modal = (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/40"
        aria-hidden="true"
        onClick={onClose}
      />
      <div className="relative w-full max-w-md rounded-2xl bg-surface-elevated border border-border shadow-2xl overflow-hidden">
        <header className="flex items-center justify-between px-5 py-3 border-b border-border bg-destructive-50">
          <div className="flex items-center gap-2 text-destructive-700">
            <TrashIcon className="h-5 w-5" />
            <h3 className="text-sm font-semibold">Delete account</h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-destructive-100"
            aria-label="Close"
          >
            <XMarkIcon className="h-5 w-5 text-destructive-700" />
          </button>
        </header>
        <div className="p-5 space-y-4">
          {step === 1 && (
            <>
              <p className="text-sm text-text-secondary">
                This will permanently delete all your likes and notifications.
                You can reactivate your account by logging back in within 30
                days, but your likes and notifications cannot be recovered.
              </p>
              <div>
                <label className="text-sm font-medium text-text-secondary">
                  Tell us why (optional)
                </label>
                <textarea
                  rows={3}
                  className="mt-1 w-full bg-surface-elevated rounded-lg border border-border-strong px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-red-600"
                  placeholder="I no longer use Traces because…"
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                />
              </div>
              <div className="flex items-center justify-end gap-2">
                <button
                  className="px-3 py-1.5 rounded-md border border-border-strong text-sm hover:bg-surface"
                  onClick={onClose}
                >
                  Cancel
                </button>
                <button
                  className="px-3 py-1.5 rounded-md bg-destructive text-text-inverse text-sm hover:bg-destructive-700"
                  onClick={() => setStep(2)}
                >
                  Continue
                </button>
              </div>
            </>
          )}
          {step === 2 && (
            <>
              <p className="text-sm text-text-secondary">
                Type <span className="font-semibold">DELETE</span> to confirm.
                After 30 days, this action cannot be undone.
              </p>
              <input
                type="text"
                value={deleteInput}
                onChange={(e) => setDeleteInput(e.target.value)}
                className="w-full bg-surface-elevated rounded-lg border border-border-strong px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-red-600 disabled:opacity-50"
                placeholder="DELETE"
                disabled={deleting}
              />
              <div className="flex items-center justify-between">
                <button
                  className="px-3 py-1.5 rounded-md border border-border-strong text-sm hover:bg-surface disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={() => setStep(1)}
                  disabled={deleting}
                >
                  Back
                </button>
                <button
                  className="px-3 py-1.5 rounded-md bg-destructive text-text-inverse text-sm hover:bg-destructive-700 disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
                  onClick={onConfirm}
                  disabled={deleteInput !== "DELETE" || deleting}
                >
                  {deleting && <Spinner size={14} />}
                  {deleting ? "Deleting..." : "Delete account"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
